import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Loader, Dimmer } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  getRohstoffe,
  updateRohstoff,
  getVistPermissions,
  setBatchingParameters,
  resetBatchingParameters,
} from '../../../actions';
import {
  canCrudRohstoffeTable,
  canReadRohstoffe,
  selectIsAdmin,
  selectPermissions,
} from '../../../helpers/permissions';
import Filters from '../Filters';

import { BasicTable } from '../../ReactTableContent/ReactTableContent';
import warningTriangle from '../icons/unvollstaendige_naehrwerte.gif';

import { createColumnHelper } from '@tanstack/react-table';

import NavHeader from '../NavHeader';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RohstoffeListing = ({ intl }) => {
  const dispatch = useDispatch();
  const currentListing = 'rohstoffe';

  const pathname = useSelector((state) => state.router.location.pathname);
  const rohstoffe = useSelector((state) => state.rohstoffe.data.rohstoffe);
  const totalPages = useSelector((state) => state.rohstoffe.data.total_pages);
  const token = useSelector((state) => state.userSession.token);
  const permissions = useSelector(selectPermissions);
  const isAdmin = useSelector(selectIsAdmin);
  const batchingParameters = useSelector((state) => state.batchingParameters);

  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);

  const canViewListing = canReadRohstoffe(permissions);
  // Rohstoffe CRUD is F&E + Admin only per TASK-90 §2.
  const showAddButton = canCrudRohstoffeTable(permissions);
  // Soft-delete view and undelete are admin-only on the backend.
  const showShowDeletedButton = isAdmin;
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // table config
  const [tableConfig, setTableConfig] = useState({
    COLUMNS: [],
    showDeleted: false,
    data: [],
    totalPages: 0,
    linkToEdit: false,
  });

  const deletedButton = !tableConfig.showDeleted
    ? 'Gelöschte Rohstoffe anzeigen'
    : 'Rohstoffe anzeigen';

  const [refreshFlag, setRefreshFlag] = useState(false);

  const columnHelper = createColumnHelper();

  const hasIncompleteNutrients = (rohstoff) => {
    if (rohstoff.bezeichnung !== 'Wasser') {
      return (
        !rohstoff.brennwert_kcal ||
        rohstoff.brennwert_kcal === 0 ||
        !rohstoff.brennwert_kj ||
        rohstoff.brennwert_kj === 0
      );
    }
    return false;
  };

  const COLUMNS = [
    columnHelper.accessor('rohstoffnummer', {
      id: 'rohstoffnummer',
      header: () => <span>Rohstoffnummer</span>,
      cell: (info) => <p>{info.getValue()}</p>,
    }),
    columnHelper.accessor('bezeichnung', {
      id: 'bezeichnung',
      header: () => <span>Bezeichnung</span>,
      cell: (info) => (
        <p>
          {hasIncompleteNutrients(info.row.original) ? (
            <img
              style={{ height: '10px', marginRight: '2px' }}
              src={warningTriangle}
              title="unvollständige Nährwerte"
            />
          ) : (
            ''
          )}
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => {
        if (info.getValue() === 1) {
          return (
            <div className="status">
              <div className="green" /> <p>Aktuell</p>
            </div>
          );
        } else if (info.getValue() === 2) {
          return (
            <div className="status">
              <div className="red" />
              <p>Stillgelegt</p>
            </div>
          );
        } else if (info.getValue() === 3) {
          return (
            <div className="status">
              <div className="yellow" />
              <p>In Bearbeitung</p>
            </div>
          );
        } else {
          return <p>{info.getValue()}</p>;
        }
      },
    }),
    columnHelper.accessor('crdate', {
      id: 'crdate',
      header: () => <span>Erstellt</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        return <p>{formattedDate}</p>;
      },
    }),
    columnHelper.accessor('modified', {
      id: 'modified',
      header: () => <span>Bearbeitet</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        if (timestamp > 0) {
          return <p>{formattedDate}</p>;
        } else {
          return <p></p>;
        }
      },
    }),
    columnHelper.accessor('benutzer', {
      id: 'benutzer',
      header: () => <span>Ersteller</span>,
      cell: (info) => <p>{info.getValue()}</p>,
    }),
  ];

  const COLUMNS_DELETED = [
    columnHelper.accessor('rohstoffnummer', {
      id: 'rohstoffnummer',
      header: () => <span>Rohstoffnummer</span>,
      cell: (info) => <p>{info.getValue()}</p>,
    }),
    columnHelper.accessor('bezeichnung', {
      id: 'bezeichnung',
      header: () => <span>Bezeichnung</span>,
      cell: (info) => <p>{info.getValue()}</p>,
    }),
    columnHelper.accessor('crdate', {
      id: 'crdate',
      header: () => <span>Erstellt</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        return <p>{formattedDate}</p>;
      },
    }),
    columnHelper.accessor('modified', {
      id: 'modified',
      header: () => <span>Bearbeitet</span>,
      cell: (info) => {
        const timestamp = parseInt(info.getValue(), 10) * 1000; // Convert Unix timestamp to milliseconds
        const date = new Date(timestamp);

        // Format the date as "dd.mm.yy hh:mm"
        const formattedDate = `${padZero(date.getDate())}.${padZero(
          date.getMonth() + 1,
        )}.${padZero(date.getFullYear() % 100)}`;

        return <p>{formattedDate}</p>;
      },
    }),
    columnHelper.accessor('deleted', {
      id: 'deleted',
      header: () => <span></span>,
      cell: (info) => (
        <Button onClick={() => unDelete(info.row.original.id)}>
          Wiederherstellen
        </Button>
      ),
    }),
  ];

  // Helper function to pad zeroes for single-digit numbers
  const padZero = (num) => (num < 10 ? `0${num}` : num);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      dispatch(getVistPermissions())
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);

  // fetch database content
  useEffect(() => {
    dispatch(
      getRohstoffe({
        showDeleted: tableConfig.showDeleted,
        batchingParameters: batchingParameters,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag, getRohstoffe, batchingParameters, tableConfig.showDeleted]);

  // attatch data to tableConfig when finished loading
  useEffect(() => {
    const columns = tableConfig.showDeleted ? COLUMNS_DELETED : COLUMNS;
    setTableConfig({
      ...tableConfig,
      data: rohstoffe,
      COLUMNS: columns,
      totalPages: totalPages,
    });
    if (
      batchingParameters &&
      batchingParameters.currentListing !== currentListing
    ) {
      dispatch(resetBatchingParameters({ currentListing: currentListing }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rohstoffe, tableConfig.showDeleted]);

  const toggleShowDeleted = () => {
    if (!isAdmin) return;
    setTableConfig({
      ...tableConfig,
      showDeleted: !tableConfig.showDeleted,
    });

    dispatch(resetBatchingParameters({ currentListing: currentListing }));
    setRefreshFlag(!refreshFlag);
  };

  const unDelete = (id) => {
    if (!isAdmin) return;
    dispatch(updateRohstoff(id, { deleted: 0 }));
    dispatch(
      getRohstoffe({
        showDeleted: true,
        batchingParameters: batchingParameters,
      }),
    );

    setRefreshFlag(!refreshFlag);
  };

  if (error) {
    return <Error error={error} />;
  }
  // check if user is logged in
  if (!token) {
    setError({ status: 401 });
  }
  // check if user has the sufficient permissions to view this page
  if (!permissionsLoading && !canViewListing) {
    setError({ status: 401 });
  }

  const searchableFields = {
    fields: [
      { label: 'Rohstoffname', value: 'rohstoffname' },
      { label: 'Zusatzinformation', value: 'zusatzinformation' },
      { label: 'Rohstoffnummer', value: 'rohstoffnummer' },
    ],
    default: { label: 'Rohstoffname', value: 'rohstoffname' },
  };
  return (
    (rohstoffe && (
      <div className="view-wrapper">
        <Helmet title="Rohstoffe" />
        <NavHeader />
        <Container className="controlpanel">
          <Segment.Group raised>
            <Segment className="primary">
              <div className="rezeptdatenbank header">
                <div className="header-container">
                  <h1>
                    {tableConfig.showDeleted
                      ? 'Gelöschte Rohstoffe'
                      : 'Rohstoffe'}{' '}
                  </h1>
                </div>
                <div className="button-container">
                  <div className="buttonArea">
                    {showAddButton && (
                      <Link to="rohstoffe/add">
                        <Button className="rdb-new" primary type="New">
                          Neuer Rohstoff
                        </Button>
                      </Link>
                    )}
                    {showShowDeletedButton && (
                      <Button
                        className="rdb-show-deleted"
                        primary
                        onClick={() => toggleShowDeleted()}
                      >
                        {deletedButton}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="rezeptdatenbank header"></div>
            </Segment>

            {rohstoffe && tableConfig.data && (
              <Segment>
                <Filters
                  filters={['status', 'ersteller', 'unv_naehr']}
                  batchingParameters={batchingParameters}
                />
                <BasicTable
                  tableConfig={tableConfig}
                  searchableFields={searchableFields}
                  setTableConfig={setTableConfig}
                  batchingParameters={batchingParameters}
                  setBatchingParameters={setBatchingParameters}
                />
              </Segment>
            )}
          </Segment.Group>
        </Container>
        {isClient && (
          <Portal node={document.getElementById('toolbar')}>
            <Toolbar
              pathname={pathname}
              hideDefaultViewButtons
              inner={
                <Link to="/controlpanel/rezeptdatenbank" className="item">
                  <Icon
                    name={backSVG}
                    className="contents circled"
                    size="30px"
                    title={intl.formatMessage(messages.back)}
                  />
                </Link>
              }
            />
          </Portal>
        )}
      </div>
    )) || (
      <Dimmer active>
        <Loader />
      </Dimmer>
    )
  );
};

RohstoffeListing.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(RohstoffeListing);
